"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useCallback } from "react";
import { useLocalization } from "@/providers/useLocalization";
import { useTranslations } from "@/providers/TranslationsProvider";
import UserAvatar from "./UserAvatar";
import dynamic from "next/dynamic";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { CONFIGS } from "@/constants/configs";
import { Logo } from "@/components/Logo";
import Modal from "@/components/Modal";
const SignInModal = dynamic(() => import("./SignInModal"), {
  ssr: false
});

/**
 * Where to go after sign-in — same-site paths only, never "//host" or a URL.
 * Checked on the parsed URL, since the browser strips tabs/newlines before
 * resolving ("/\t/evil.example" becomes "//evil.example").
 */
const NEXT_BASE = "https://rawej.invalid";
export function safeNext(next: string | null): string | null {
  if (!next?.startsWith("/")) {
    return null;
  }
  try {
    const url = new URL(next, NEXT_BASE);
    return url.origin === NEXT_BASE ? url.pathname + url.search + url.hash : null;
  } catch {
    return null;
  }
}
const Header: React.FC = () => {
  const $ = _c(67);
  const {
    config,
    language
  } = useLocalization();
  const {
    t
  } = useTranslations();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const isRtl = config.direction === "rtl";
  const [showSignIn, setShowSignIn] = useState(false);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("auth");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const authParam = t0;
  const isUrlAuth = authParam === "phone" || authParam === "email" || authParam === "otp";
  let t1;
  bb0: {
    if (isUrlAuth) {
      const t2 = authParam as "phone" | "email" | "otp";
      let t3;
      if ($[2] !== searchParams) {
        t3 = searchParams.get("to") || "";
        $[2] = searchParams;
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      let t4;
      if ($[4] !== searchParams) {
        t4 = searchParams.get("countryCode") || "+98";
        $[4] = searchParams;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      let t5;
      if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
        t5 = {
          step: t2,
          phone: t3,
          countryCode: t4
        };
        $[6] = t2;
        $[7] = t3;
        $[8] = t4;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      t1 = t5;
      break bb0;
    }
    let t2;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        step: "phone" as const,
        phone: "",
        countryCode: "+98"
      };
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = t2;
  }
  const initialData = t1;
  const showSignInModal = showSignIn || isUrlAuth;
  let t2;
  if ($[11] !== searchParams) {
    t2 = searchParams.get("meet");
    $[11] = searchParams;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const meetParam = t2;
  let t3;
  if ($[13] !== searchParams) {
    t3 = safeNext(searchParams.get("next"));
    $[13] = searchParams;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const next = t3;
  let t4;
  if ($[15] !== meetParam || $[16] !== t) {
    t4 = meetParam === "not-invited" ? t("auth.session not booked for account", "This session isn't booked on the account you're signed in with. Sign in with the mobile number or email you booked with.") : undefined;
    $[15] = meetParam;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const signInNotice = t4;
  let t5;
  if ($[18] !== meetParam || $[19] !== t) {
    t5 = meetParam === "unavailable" ? t("auth.session unavailable", "This session is no longer available \u2014 it may have been cancelled.") : meetParam === "failed" ? t("auth.session open failed", "We couldn't open your session. Please try your link again in a moment.") : null;
    $[18] = meetParam;
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const meetMessage = t5;
  let t6;
  if ($[21] !== pathname || $[22] !== router || $[23] !== searchParams) {
    t6 = () => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("meet");
      const query = params.toString();
      router.replace(`${pathname}${query ? `?${query}` : ""}`);
    };
    $[21] = pathname;
    $[22] = router;
    $[23] = searchParams;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const handleCloseMeetMessage = t6;
  let t7;
  if ($[25] !== pathname || $[26] !== router || $[27] !== searchParams) {
    t7 = () => {
      setShowSignIn(false);
      if (searchParams.get("auth")) {
        const params_0 = new URLSearchParams(searchParams.toString());
        params_0.delete("auth");
        params_0.delete("to");
        params_0.delete("countryCode");
        params_0.delete("next");
        params_0.delete("meet");
        const query_0 = params_0.toString();
        router.replace(`${pathname}${query_0 ? `?${query_0}` : ""}`);
      }
    };
    $[25] = pathname;
    $[26] = router;
    $[27] = searchParams;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const handleCloseSignIn = t7;
  let t8;
  if ($[29] !== language || $[30] !== next) {
    t8 = () => {
      window.location.href = next ?? `/${language}`;
    };
    $[29] = language;
    $[30] = next;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const handleSuccess = t8;
  let t9;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      height: "72px"
    };
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== language || $[34] !== router) {
    t10 = () => router.push(`/${language}`);
    $[33] = language;
    $[34] = router;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Logo className="transition-all duration-300 group-hover:scale-110 drop-shadow-sm" size={32} />;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = CONFIGS.app.displayName[language as keyof typeof CONFIGS.app.displayName] || CONFIGS.app.displayName.en;
  let t13;
  if ($[37] !== t12) {
    t13 = <div className="flex flex-col"><h1 className="text-[18px] font-bold text-[#1D1D1F] dark:text-[#EFF3F4] tracking-tight leading-none">{t12}</h1></div>;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t10 || $[40] !== t13) {
    t14 = <div className="flex items-center gap-1 cursor-pointer group" onClick={t10}>{t11}{t13}</div>;
    $[39] = t10;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const t15 = `flex items-center gap-2 ${isRtl ? "flex-row-reverse" : "flex-row"}`;
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <UserAvatar refetchTrigger={0} onSignInClick={() => setShowSignIn(true)} />;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15) {
    t17 = <div className={t15}>{t16}</div>;
    $[43] = t15;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== next || $[46] !== t) {
    t18 = next?.startsWith("/meet/") ? t("auth.opening session", "Opening your session\u2026") : undefined;
    $[45] = next;
    $[46] = t;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== handleCloseSignIn || $[49] !== handleSuccess || $[50] !== initialData.countryCode || $[51] !== initialData.phone || $[52] !== initialData.step || $[53] !== showSignInModal || $[54] !== signInNotice || $[55] !== t18) {
    t19 = <SignInModal show={showSignInModal} onClose={handleCloseSignIn} onSuccess={handleSuccess} initialStep={initialData.step} initialPhone={initialData.phone} initialCountryCode={initialData.countryCode} notice={signInNotice} redirectingText={t18} />;
    $[48] = handleCloseSignIn;
    $[49] = handleSuccess;
    $[50] = initialData.countryCode;
    $[51] = initialData.phone;
    $[52] = initialData.step;
    $[53] = showSignInModal;
    $[54] = signInNotice;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const t20 = !!meetMessage && !showSignInModal;
  let t21;
  if ($[57] !== handleCloseMeetMessage || $[58] !== meetMessage || $[59] !== t20) {
    t21 = <Modal show={t20} onClose={handleCloseMeetMessage} variant="message" type="warning" mobileCenter={true}>{meetMessage}</Modal>;
    $[57] = handleCloseMeetMessage;
    $[58] = meetMessage;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== config.direction || $[62] !== t14 || $[63] !== t17 || $[64] !== t19 || $[65] !== t21) {
    t22 = <header className="w-full px-8 flex items-center justify-between fixed top-0 left-0 right-0 z-[2000] bg-[var(--header-bg)] backdrop-blur-md border-b border-[var(--border-subtle)] shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all duration-300" dir={config.direction} style={t9}>{t14}{t17}{t19}{t21}</header>;
    $[61] = config.direction;
    $[62] = t14;
    $[63] = t17;
    $[64] = t19;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
};
export default Header;